"use client";

import * as React from "react";
import { cva } from "../../lib/cva";
import { Box } from "../../components/ui/box";
import { cn } from "../../lib/utils";
import { glyphComponent, Icon, ICON_STROKE, type GlyphName, type IconSize } from "../../components/ui/icon";
import { Text } from "../../components/ui/text";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../../components/ui/tooltip";
import { colorVar, type Color } from "../../lib/colors";
import type { SurfaceToken } from "../../components/ui/box-classes";
import {
  AVATAR_SIZE_PX,
  EntityAvatar,
  EntityAvatarFallback,
  EntityAvatarImage,
  getInitials,
  type EntityAvatarPresence,
  type EntityAvatarSize,
} from "./entity-avatar";
import type { Omittable } from "../../lib/props";

/**
 * Stacked avatar group with overlapping circles and a "+N" overflow chip.
 *
 * The separation rings and monogram-tile tint bake are both painted in the
 * color of the surface the group sits on, so the slots read as cutouts of it.
 * That surface is the `surface` prop: `background` — the page — covers tables,
 * lists and screens, and a group inside a dialog or menu passes `popover`.
 */

export type EntityGroupItem = Omittable<{
  id: string;
  name?: string | null;
  imageUrl?: string | null;
  /** Solid key-color fill for the initials fallback (avatar slots only) —
   *  see EntityAvatarFallback.color. Photos and gray states win. */
  avatarColor?: Color | null;
  /** Glyph rendered muted on the neutral wash when `name` yields no initials
   *  (avatar slots only) — the person glyph unless the item names its own
   *  kind's. */
  fallbackIcon?: GlyphName | null;
  /** When set, the slot renders a layered identity glyph instead of an avatar. */
  icon?: GlyphName | null;
  /** Letter-monogram slot: renders the character on the tile in the glyph's
   *  place. Wins over `icon` when both are set. */
  monogram?: string | null;
  /** Color name from the shared scale. Only used when `icon`/`monogram` is set. */
  iconColor?: Color | null;
  /** Painted fill behind the mark. A monogram slot defaults to `soft`; an
   *  icon slot paints a tile ONLY when this is set (an avatar-kind entity's
   *  picked glyph on its tile), else it is the bare identity glyph. */
  iconTile?: "soft" | "solid" | null;
  /** Suspended treatment — greyscale + dimmed, matching EntityAvatar. */
  muted?: boolean | null;
  /** Invited treatment — dashed ring + reduced opacity, matching EntityAvatar. */
  invited?: boolean | null;
  /** Live presence indicator on the avatar's corner (avatar slots only) —
   *  ignored for icon tiles and the count chip. */
  presence?: EntityAvatarPresence | null;
  /** The avatar slot's outline (avatar slots only): a `square` plate for an
   *  organization's logo among the circles people wear, so the shape alone
   *  says company or person. It counts as a tiled slot for the "+N" chip's
   *  shape. Icon tiles are square already. @default "circle" */
  shape?: "circle" | "square" | null;
}>;

export type EntityGroupSize = "2xs" | "xs" | "sm" | "default";

export type EntityGroupProps = Omittable<{
  items: EntityGroupItem[];
  /** Maximum number of stacked slots to display — "stack" variant only
   *  ("expanded" always renders the first item plus a count chip). When item
   *  count exceeds this, the last slot becomes a circular count chip. Default: 3 */
  max?: number;
  /**
   * The size of the set `items` is a sample OF, when the caller could load
   * only the first few (a server-bounded preview). The overflow chip counts
   * against this, so three faces out of forty read "+37" instead of "+0" —
   * a number the group has no other way to know. Defaults to `items.length`,
   * which is what a caller holding the whole set should leave it at.
   */
  total?: number;
  /** Slot size. Default: "sm" */
  size?: EntityGroupSize;
  /**
   * Layout variant.
   * - "stack" (default): overlapping circles with a circular count chip tail.
   * - "expanded": first item's visual + name in a pill, with a circular count
   *   chip overlapping the pill's right edge for the rest.
   */
  variant?: "stack" | "expanded";
  /** The surface the group is mounted on. Drives the separation rings and the
   *  monogram-tile tint bake. Default: "background". */
  surface?: SurfaceToken;
  /**
   * Wraps each VISIBLE slot's content (inside the group-owned ring/overlap
   * chrome) — the hook consumers use to attach per-item hover previews. The
   * index matches the `items` order.
   */
  wrapItem?: (
    node: React.ReactNode,
    item: EntityGroupItem,
    index: number,
  ) => React.ReactNode;
  /**
   * Wraps the overflow count chip's content — e.g. a hover list of the hidden
   * items. `count` is the exact number the chip stands for: the chip itself
   * caps at "9+", so a wrapper that names the number is the only place the
   * true count survives. Absent, the chip carries a plain "N more" tooltip;
   * a wrapper REPLACES that (return `node` bare to render no hover at all).
   */
  wrapOverflow?: (node: React.ReactNode, count: number) => React.ReactNode;
  /**
   * A slot rendered after the last item (and after the overflow chip), sharing
   * the stack's overlap and separation ring — the group's one extension point
   * for an action that acts on the SET, such as `EntityGroupAddSlot`. It
   * occupies a real slot in the width math, so nothing behind it is clipped.
   *
   * With no items it becomes the whole group, replacing the static empty
   * glyph: a set you can add to has an affordance, not a picture of one.
   */
  trailing?: React.ReactNode;
}>;

// Box must match the avatar px size so the clipping wrapper never crops it.
// `2xs` (16px) is the chip-value rung: a stack riding inside a 24px control
// (toolbar filter chips) — one initial per monogram at this size.
const BOX_SIZE: Record<EntityGroupSize, number> = {
  "2xs": AVATAR_SIZE_PX["2xs"],
  xs: AVATAR_SIZE_PX.xs,
  sm: AVATAR_SIZE_PX.sm,
  default: AVATAR_SIZE_PX.default,
};
const AVATAR_SIZE: Record<EntityGroupSize, EntityAvatarSize> = {
  "2xs": "2xs",
  xs: "xs",
  sm: "sm",
  default: "default",
};
const ICON_SIZE = { "2xs": "2xs", xs: "xs", sm: "sm", default: "md" } as const;
// How far each stacked slot rides over the one before it. Flat across sizes:
// the separation ring is 1.5px at every rung, so a 2px overlap leaves a
// hairline of ring between neighbours — the faces touch, they do not squeeze.
const STACK_OVERLAP_PX = 2;
// The trailing action does not ride over the slot before it: a press target
// laid over the count chip covered the end of "+7". It stands a hairline clear
// instead (its separation ring still shows between the two).
const TRAILING_GAP_PX = 2;
const ROUNDED = { "2xs": "sm", xs: "sm", sm: "sm", default: "md" } as const;
const FALLBACK_TEXT_SIZE = {
  "2xs": "2xs",
  xs: "2xs",
  sm: "xs",
  default: "xs",
} as const;

/**
 * Ring effect around overlapping circles. A box-shadow, not a border, so the
 * ring sits outside the element and does not shrink the content area. It is
 * painted in the surface's own fill, so the circles read as cutouts of it.
 * Written out per surface — Tailwind only emits classes it can see whole.
 */
export const entityGroupVariants = cva("", {
  variants: {
    surface: {
      sunken: "shadow-[0_0_0_1.5px_var(--sunken)]",
      background: "shadow-[0_0_0_1.5px_var(--background)]",
      card: "shadow-[0_0_0_1.5px_var(--card)]",
      popover: "shadow-[0_0_0_1.5px_var(--popover)]",
      sidebar: "shadow-[0_0_0_1.5px_var(--sidebar)]",
      elevated: "shadow-[0_0_0_1.5px_var(--elevated)]",
    } satisfies Record<SurfaceToken, string>,
  },
  defaultVariants: {
    surface: "background",
  },
});

/** An entity's color as a paintable `var()`, falling back to the muted foreground when
 *  the entity has none. Callers narrow a stored string with `toColor` first. */
export function resolveEntityColor(color?: Color | null): string {
  return color ? colorVar(color) : "var(--muted-foreground)";
}

/** Bake the 15%-tint appearance into a solid paint by mixing the color over
 *  `surface`. Solid, so stacked chips never show through. */
export function bakeEntityTint(
  color?: Color | null,
  surface: SurfaceToken = "background",
): string {
  return `color-mix(in oklab, ${resolveEntityColor(color)} 15%, var(--${surface}))`;
}

// Monogram letter size per glyph size — the letter sits in the glyph's place,
// so it keys off the same `size` the icon would use.
const MONOGRAM_TEXT_SIZE: Record<
  NonNullable<React.ComponentProps<typeof Icon>["size"]>,
  React.ComponentProps<typeof Text>["size"]
> = {
  "2xs": "2xs",
  xs: "2xs",
  sm: "xs",
  md: "xs",
  lg: "sm",
  xl: "lg",
  "2xl": "xl",
};

// Bare identity glyphs have no surrounding tile. Give them one more size
// rung while preserving the original slot.
const BARE_GLYPH_SIZE: Record<IconSize, IconSize> = {
  "2xs": "xs",
  xs: "sm",
  sm: "md",
  md: "lg",
  lg: "xl",
  xl: "2xl",
  "2xl": "2xl",
};

export type EntityIconStyle = "duotone-rounded";

// Glyph edge inside a painted tile, by tile edge. The mark keeps roughly the
// room a monogram letter has — a touch over two thirds of the tile on the
// small rungs, easing toward 56% on the large ones so a wide glyph never
// crowds the corners. TabsViewIcon draws the same 12-in-14 and 20-in-24 pairs.
const CONTAINED_GLYPH_PX: ReadonlyArray<readonly [tileMax: number, glyphPx: number]> = [
  [14, 10],
  [16, 12],
  [20, 14],
  [24, 16],
  [32, 20],
  [48, 28],
];
const CONTAINED_GLYPH_PX_MAX = 36;

function containedGlyphPx(tileEdge: number): number {
  for (const [tileMax, glyphPx] of CONTAINED_GLYPH_PX) {
    if (tileEdge <= tileMax) return glyphPx;
  }
  return CONTAINED_GLYPH_PX_MAX;
}

// Stroke for a glyph rendered at an arbitrary px, off the same ladder the
// size aliases use: 2 through 14px, 1.75 at 16px, 1.5 from 20px up.
function containedStroke(glyphPx: number): number {
  if (glyphPx <= 14) return ICON_STROKE["2xs"];
  if (glyphPx <= 16) return ICON_STROKE.sm;
  return ICON_STROKE.lg;
}

export type EntityIconTileProps = Omittable<{
  icon?: GlyphName | null;
  /** Letter-monogram tile: renders the (single, uppercased) character in the
   *  glyph's place. Wins over `icon` when both are set. */
  monogram?: string | null;
  /** Color name from the shared scale. */
  color?: Color | null;
  /** Local-duotone identity treatment. Default: "duotone-rounded". */
  iconStyle?: EntityIconStyle;
  /** Glyph size for the BARE identity glyph (the monogram letter keys off the
   *  same scale). A contained glyph sizes off the tile edge instead. Default "sm". */
  size?: React.ComponentProps<typeof Icon>["size"];
  /** Alignment slot edge in px. */
  boxSize: number;
  /** Tile edge in px when it should run smaller than its slot. */
  tileSize?: number;
  /** Tile corner rounding. Default "sm" (6px); "xs" (4px) keeps a small
   *  inset tile square-shouldered — 6px on a 14px tile reads circular. */
  rounded?: "xs" | "sm" | "md";
  /** Painted fill behind the mark. A monogram always paints one (default
   *  `soft` — a bare letter is not a mark). An icon paints one ONLY when a
   *  value is passed, mounting the glyph on the tile in contrast ink the way
   *  a monogram letter sits; left unset, an icon is the bare identity glyph. */
  tile?: "soft" | "solid";
  /** The surface the tile is mounted on. Default: "background". */
  surface?: SurfaceToken;
  /** The entity's name, when the tile stands for it alone (a face in a
   *  stack). The tile is then an image with that name. Beside a written
   *  name, leave it out: the tile is decorative there. */
  label?: string;
}>;

/** The canonical icon/letter visual for icon-kind entities. Icons use a
 *  Duotone Rounded glyph with no painted container unless a `tile` is
 *  requested. Monograms always retain a solid or soft tile because the
 *  container gives a letter enough identity to stand alone. */
export function EntityIconTile({
  icon,
  monogram,
  color,
  size = "sm",
  boxSize,
  tileSize,
  rounded = "sm",
  tile,
  surface = "background",
  label,
}: EntityIconTileProps): React.ReactElement {
  const named = label ? ({ role: "img", "aria-label": label } as const) : undefined;
  if (!monogram && tile === undefined) {
    const identityIconProps = {
      name: icon ?? "circle",
      size: BARE_GLYPH_SIZE[size],
      color: color ?? "muted",
    } as const;
    return (
      <Box
        width={boxSize}
        height={boxSize}
        direction="row"
        align="center"
        justify="center"
        shrink={false}
        data-slot="entity-icon-visual"
        {...named}
      >
        <Icon {...identityIconProps} />
      </Box>
    );
  }

  const solid = tile === "solid";
  const tileEdge = tileSize ?? boxSize;
  // An unwritable monogram (whitespace) falls through to the glyph mark —
  // never a "?" character.
  const letter = monogram?.trim().charAt(0).toUpperCase();
  let mark: React.ReactNode;
  if (letter) {
    mark = (
      <Text
        // The letter is the name's first letter: it adds nothing a screen
        // reader should say ("R Roof Crew"). The name beside it, or the
        // container's label, names the entity.
        aria-hidden
        size={MONOGRAM_TEXT_SIZE[size]}
        weight="semibold"
        leading="none"
        // Solid tiles inherit the contrast ink from the tile's text-c-foreground.
        color={solid ? undefined : (color ?? "muted")}
      >
        {letter}
      </Text>
    );
  } else {
    // A contained glyph draws straight from the registry at a px edge the
    // size aliases do not offer; inline dimensions keep it clear of any
    // descendant `svg` size pin the host control carries. On a solid tile it
    // inherits the tile's contrast ink; on a soft one it takes the key color.
    const name = icon ?? "circle";
    const Glyph = glyphComponent(name);
    const glyphPx = containedGlyphPx(tileEdge);
    mark = (
      <Glyph
        aria-hidden
        className={cn("shrink-0", !solid && "text-c")}
        color="currentColor"
        data-color={solid ? undefined : (color ?? "muted")}
        data-icon-name={name}
        data-icon-style="salestrak-outline"
        data-slot="icon"
        size={glyphPx}
        strokeWidth={containedStroke(glyphPx)}
        style={{ width: glyphPx, height: glyphPx }}
      />
    );
  }
  const painted = (
    <Box
      width={tileEdge}
      height={tileEdge}
      rounded={rounded}
      overflow="hidden"
      direction="row"
      align="center"
      justify="center"
      shrink={false}
      data-color={solid && color ? color : undefined}
      background={solid && !color ? "muted" : undefined}
      className={cn(solid && color && "bg-c text-c-foreground")}
      style={solid ? undefined : { backgroundColor: bakeEntityTint(color, surface) }}
      {...(tileSize == null ? named : undefined)}
    >
      {mark}
    </Box>
  );
  if (tileSize == null) return painted;
  return (
    <Box
      width={boxSize}
      height={boxSize}
      direction="row"
      align="center"
      justify="center"
      shrink={false}
      {...named}
    >
      {painted}
    </Box>
  );
}

/** Overflow display caps at "9+" — beyond nine the precise count stops being scannable. */
function formatOverflow(count: number): string {
  return count > 9 ? "9+" : `+${count}`;
}

export type EntityGroupAddSlotProps = Omittable<{
  /** Slot size — match the group's own `size` so the circle lines up with the
   *  avatars it trails. Default: "sm" */
  size?: EntityGroupSize;
  ref?: React.Ref<HTMLButtonElement>;
}> &
  Omit<React.ComponentPropsWithoutRef<"button">, "children">;

/**
 * The add affordance that trails an `EntityGroup` — a dashed outline the size
 * of a slot, holding the add glyph.
 *
 * Dashed because the slot is a place a member could go rather than one that is
 * filled: the same reading `EntityAvatar invited` uses for a seat that is
 * spoken for but empty. The glyph carries the person and the plus together;
 * at the 16–24px rungs a stack actually renders, a separate corner badge is an
 * 8px mark needing its own offset per shape, which is why nothing here badges
 * an identity slot.
 *
 * It renders a real `button`, so an overlay trigger composes onto it with
 * `render` and a tooltip wraps it — the caller owns what pressing it does.
 */
export function EntityGroupAddSlot({
  size = "sm",
  className,
  type = "button",
  ref,
  ...props
}: EntityGroupAddSlotProps): React.ReactElement {
  const boxSize = BOX_SIZE[size];
  return (
    <Box
      as="button"
      type={type}
      ref={ref}
      width={boxSize}
      height={boxSize}
      rounded="full"
      direction="row"
      align="center"
      justify="center"
      shrink={false}
      cursor="pointer"
      // Opaque, like the `Avatar` it stacks against: an outline slot is still
      // a slot, and a transparent one lets the avatar it overlaps read
      // straight through the dashes. Same fill Avatar paints, so a mixed row
      // of faces and this one stays uniform.
      background="background"
      data-slot="entity-group-add"
      className={cn(
        "border border-dashed border-muted-foreground/60 transition-colors hover:border-muted-foreground",
        // The hover wash rides a gradient layer OVER that fill (the `outline`
        // Button and table-row `--row-wash` mechanic) instead of replacing
        // it. A plain `hover:bg-wash-hover` is the same CSS property as the
        // fill, so it SWAPS it — and this slot overlaps an avatar, so the
        // swap would put the face it covers straight back through the dashes
        // for exactly as long as the pointer is on it.
        "[--entity-group-add-wash:transparent] bg-linear-to-b from-(--entity-group-add-wash) to-(--entity-group-add-wash) hover:[--entity-group-add-wash:var(--wash-hover)]",
        className,
      )}
      {...props}
    >
      <Icon name="user-plus" size={ICON_SIZE[size]} color="muted" />
    </Box>
  );
}

export function EntityGroup({
  items,
  total: totalProp,
  max = 3,
  size = "sm",
  variant = "stack",
  surface = "background",
  wrapItem,
  wrapOverflow,
  trailing,
}: EntityGroupProps): React.ReactElement {
  const applyWrap = (node: React.ReactNode, item: EntityGroupItem, index: number) =>
    wrapItem ? wrapItem(node, item, index) : node;
  // The default hover restores the exact count the "9+" cap hides. A consumer
  // supplying its own wrapper owns the chip's hover outright — two stacked
  // hover layers on one 24px target would fight.
  const applyOverflowWrap = (node: React.ReactNode, count: number) =>
    wrapOverflow ? (
      wrapOverflow(node, count)
    ) : (
      <Tooltip>
        <TooltipTrigger
          render={<Box direction="row" align="center" justify="center" fill="size" />}
        >
          {node}
        </TooltipTrigger>
        <TooltipPopup>{`${count} more`}</TooltipPopup>
      </Tooltip>
    );
  // Destructured rather than length-tested: the leading slot is what the
  // `expanded` variant renders from, and this is the one check that proves it
  // is there.
  const [first] = items;
  if (first === undefined) {
    // A trailing action IS the empty state when there is one — the same glyph,
    // but pressable. Without one the group still says "nobody" in that glyph's
    // own vocabulary.
    if (trailing) {
      return (
        <Box direction="row" align="center" shrink={false} data-slot="entity-group">
          {trailing}
        </Box>
      );
    }
    return <Icon name="user-plus" size="md" color="muted" />;
  }

  const boxSize = BOX_SIZE[size];
  const avatarSize = AVATAR_SIZE[size];
  const iconSize = ICON_SIZE[size];
  const rounded = ROUNDED[size];
  const fallbackTextSize = FALLBACK_TEXT_SIZE[size];

  // User items render the circular avatar treatment. Icon items use a
  // Duotone Rounded glyph, or sit on a tile when the item asks for one.
  // Monogram items retain their letter tile.
  const isIconSlot = (item: EntityGroupItem) =>
    (!!item.icon || !!item.monogram) && !item.imageUrl;

  // Only tiled slots and square plates carry a square shape. A bare glyph has
  // no surrounding shape, so its overflow count uses the neutral circular
  // treatment.
  const isTiledSlot = (item: EntityGroupItem) =>
    ((!!item.monogram || (!!item.icon && !!item.iconTile)) && !item.imageUrl) ||
    (!isIconSlot(item) && item.shape === "square");
  const allTiledSlots = items.every(isTiledSlot);
  // The clipping wrapper's radius: the size-matched square for a tile or a
  // plate, the circle for a face.
  const slotRounded = (item: EntityGroupItem) =>
    isTiledSlot(item) ? rounded : ("full" as const);

  const renderSlot = (item: EntityGroupItem) => {
    if (isIconSlot(item)) {
      return (
        <EntityIconTile
          icon={item.icon}
          monogram={item.monogram}
          color={item.iconColor}
          size={iconSize}
          boxSize={boxSize}
          rounded={rounded}
          tile={item.iconTile ?? (item.monogram ? "soft" : undefined)}
          surface={surface}
          label={item.name ?? undefined}
        />
      );
    }
    const initials = getInitials(item.name, avatarSize);
    return (
      <EntityAvatar
        size={avatarSize}
        shape={item.shape ?? "circle"}
        muted={!!item.muted}
        invited={!!item.invited}
        alt={item.name ?? "Avatar"}
        presence={item.presence}
      >
        {item.imageUrl ? (
          <EntityAvatarImage src={item.imageUrl} alt={item.name ?? "Avatar"} />
        ) : null}
        <EntityAvatarFallback
          color={initials ? item.avatarColor : undefined}
          icon={initials ? undefined : (item.fallbackIcon ?? "user")}
        >
          {initials ? (
            <Text size={fallbackTextSize} weight="bold">
              {initials}
            </Text>
          ) : null}
        </EntityAvatarFallback>
      </EntityAvatar>
    );
  };

  // No color override — the fallback's default keeps the count chip the same
  // color as the avatar fallbacks beside it.
  // At the 16px rung two glyphs at the fallback's 10px overrun the circle's
  // chord, and the clip cut "9+" to "9": the count steps down a size there.
  const renderCountChip = (count: number) => (
    <EntityAvatar
      size={avatarSize}
      shape={allTiledSlots ? "square" : "circle"}
      alt={`${count} more`}
    >
      <EntityAvatarFallback>
        <Text
          size={fallbackTextSize}
          weight="bold"
          className={size === "2xs" ? "text-[8px] tracking-tighter" : undefined}
        >
          {formatOverflow(count)}
        </Text>
      </EntityAvatarFallback>
    </EntityAvatar>
  );

  // The counter's clipping wrapper mirrors the chip's own shape so the ring
  // hugs a square counter instead of cutting a circle out of it.
  const overflowRounded = allTiledSlots ? rounded : ("full" as const);

  const ringClass = entityGroupVariants({ surface });

  if (variant === "expanded") {
    const overflow = items.length - 1;
    const firstIsIcon = isIconSlot(first);
    const firstIsGray = !!first.muted || !!first.invited;
    // The capsule is a darker shade of the leading slot's own color — the
    // avatar fallback's primary for users, the icon tint for icon kinds — so
    // the pill reads as one tinted unit, not avatar-on-gray.
    const pillColor = firstIsGray
      ? "var(--muted)"
      : bakeEntityTint(firstIsIcon ? first.iconColor : "primary", surface);
    return (
      <Box direction="row" align="center" shrink={false}>
        {applyWrap(
          <Box
            direction="row"
            align="center"
            gap="xs"
            rounded={slotRounded(first)}
            overflow="hidden"
            shrink={false}
            paddingRight={first.name ? "sm" : undefined}
            style={{ backgroundColor: pillColor }}
          >
            {renderSlot(first)}
            {first.name ? (
              <Text size="sm" weight="medium" truncate>
                {first.name}
              </Text>
            ) : null}
          </Box>,
          first,
          0,
        )}
        {overflow > 0 ? (
          <Box marginLeftNegative="2xs"
            width={boxSize}
            height={boxSize}
            rounded={overflowRounded}
            overflow="hidden"
            shrink={false}
            className={cn(ringClass)}
          >
            {applyOverflowWrap(renderCountChip(overflow), overflow)}
          </Box>
        ) : null}
        {trailing ? (
          <Box
            marginLeftNegative="2xs"
            width={boxSize}
            height={boxSize}
            rounded="full"
            direction="row"
            align="center"
            justify="center"
            className={cn(ringClass)}
          >
            {trailing}
          </Box>
        ) : null}
      </Box>
    );
  }

  // A preview can never be larger than the set it samples, so a `total` below
  // the loaded count is a caller bug, not a state to render — clamp rather
  // than emit a negative overflow.
  const total = Math.max(totalProp ?? items.length, items.length);
  const hasOverflow = total > max;
  const visible = hasOverflow ? items.slice(0, max - 1) : items;
  const overflowCount = hasOverflow ? total - visible.length : 0;
  const step = boxSize - STACK_OVERLAP_PX;

  // An empty group returned above, and an overflowing one always keeps the
  // count chip, so there is always at least one slot. A trailing action takes
  // a slot of its own — reserved here, or the stack's fixed width clips it.
  const filledSlots = visible.length + (hasOverflow ? 1 : 0);
  const totalSlots = filledSlots + (trailing ? 1 : 0);
  const trailingGap = trailing ? STACK_OVERLAP_PX + TRAILING_GAP_PX : 0;
  const containerWidth = boxSize + step * (totalSlots - 1) + trailingGap;

  return (
    <Box
      position="relative"
      width={containerWidth}
      height={boxSize}
      shrink={false}
      data-slot="entity-group"
    >
      {visible.map((item, index) => (
        <Box
          key={`${item.id}-${index}`}
          position="absolute"
          top={0}
          left={index * step}
          width={boxSize}
          height={boxSize}
          rounded={slotRounded(item)}
          overflow="hidden"
          direction="row"
          align="center"
          justify="center"
          className={ringClass}
        >
          {applyWrap(renderSlot(item), item, index)}
        </Box>
      ))}
      {hasOverflow ? (
        <Box
          position="absolute"
          top={0}
          left={visible.length * step}
          width={boxSize}
          height={boxSize}
          rounded={overflowRounded}
          overflow="hidden"
          direction="row"
          align="center"
          justify="center"
          className={ringClass}
        >
          {applyOverflowWrap(renderCountChip(overflowCount), overflowCount)}
        </Box>
      ) : null}
      {trailing ? (
        // No `overflow="hidden"` — the trailing slot is interactive, and a
        // clipping wrapper would crop its focus ring.
        <Box
          position="absolute"
          top={0}
          left={filledSlots * step + trailingGap}
          width={boxSize}
          height={boxSize}
          rounded="full"
          direction="row"
          align="center"
          justify="center"
          className={ringClass}
        >
          {trailing}
        </Box>
      ) : null}
    </Box>
  );
}
